// React
import React from 'react'

// Types
import { ProjectProgressReportEditAfterReviewType } from './projectProgressReportEditAfterReview'

// Constants
import * as Constants from '../../../../../constants/constants'
import constUserGroup from '../../../../../constants/userGroup'

// Stores
import useAuthenStore from '../../../../../stores/authen/authenStore'
import useAppFolderAndTabStore from '../../../../../stores/appFolderAndTab/appFolderAndTabStore'
import useAppFolderAndTabAction from '../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import useManageProjectStore from '../../../../../stores/manageProject/useManageProjectStore'

// Hooks / Utilities
import useFolderAndTab from '../../useFolderAndTab'
import useCheckPermission from '../../../../../utilities/checkPermission'
import useWebService from '../../../../../services/web/useWebService'
import useTranslate from '../../../../../i18n/useTranslate'
import useLoading from '../../../../../control/loading/useLoading'

// Routing
import { RoutePath } from '../../../../../router/routesConfig'
import { useHistory } from 'react-router-dom'

// Dialog / Types
import { DialogType } from '../../../../../control/dialog/dialog'
import * as REQServiceType from '../../../../../services/web/types/requestWebServiceTypes'

// Components
import CloseProjectOfficer from '../../page/closeProjectOfficer/closeProjectOfficer'

const useProjectProgressReportEditAfterReview = (props: ProjectProgressReportEditAfterReviewType) => {
    const { projectMode } = props
    const authenStore = useAuthenStore()
    const manageProjectStore = useManageProjectStore()
    const appFolderAndTabStore = useAppFolderAndTabStore()
    const appFolderAndTabAction = useAppFolderAndTabAction()

    const tabAndFolderHook = useFolderAndTab()
    const callWebService = useWebService()
    const history = useHistory()
    const checkPermission = useCheckPermission()
    const translate = useTranslate()
    const loadingHook = useLoading()

    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = React.useRef<Record<string, Function>>()

    const isFirstRender = React.useRef<boolean>(true)

    const isViewMode = checkPermission.chkProjectStepViewMode(
        manageProjectStore.projectData ? manageProjectStore.projectData : undefined,
    )

    React.useEffect(() => {
        console.log('####Project Progress Report Edit After Review')
    }, [])
    React.useEffect(() => {
        if (isFirstRender) {
            isFirstRender.current = false
            const isReloadProjectData = false
            const ignoreCustomFolder = false
            loadTabAndFolder(appFolderAndTabStore, isReloadProjectData, ignoreCustomFolder)
        }
    }, [authenStore.profile.userId, manageProjectStore.projectData?.submittedID ?? null])

    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }

    const loadTabAndFolder = async (
        appFolderAndTabInfo: typeof appFolderAndTabStore,
        isReloadProjectData: boolean = true,
        ignoreCustomFolder: boolean = false,
    ) => {
        const mode = projectMode
        if (mode === 'CREATE') {
            tabAndFolderHook.createProjectLoadTabAndFolder()
        } else if (mode === 'EDIT' && manageProjectStore.projectData?.projectID) {
            tabAndFolderHook.loadTabAndFolder(
                manageProjectStore.projectData,
                appFolderAndTabInfo,
                isReloadProjectData,
                ignoreCustomFolder,
            )
        }
    }

    const handleCallbackSaveComplete = (status: number, isDraft: boolean) => {
        if (status === 1) {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => {
                    handleAfterClose(isDraft)
                },
            })
        } else if (status === -2) {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.WARNING'),
                content: translate.get('ALERT.CONTENT.SECRET_PASSWORD_INCORRECT'),
                type: 'alert-message',
            })
        } else {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.ERROR'),
                content: translate.get('ALERT.CONTENT.SAVE_NOT_SUCCESS'),
                type: 'alert-message',
            })
        }
        dialogRef.current?.openDialog()
    }
    const handleAfterClose = (isDraft: boolean) => {
        const { userGroupId } = authenStore.profile
        const { tabSelected } = appFolderAndTabStore
        if (!isDraft) {
            switch (userGroupId) {
                case constUserGroup.president:
                case constUserGroup.vicePresident:
                case constUserGroup.secretary:
                case constUserGroup.reviewer:
                    if (
                        tabSelected.id === Constants.menuTab.tab2Chairman.id ||
                        tabSelected.id === Constants.menuTab.tab6Chairman.id ||
                        tabSelected.id === Constants.menuTab.tab9.id
                    ) {
                        history.push(RoutePath.manageProject)
                    } else {
                        reloadProcess()
                    }
                    break
                case constUserGroup.committee:
                    if (tabSelected.id === Constants.menuTab.tab5Commitee.id) {
                        history.push(RoutePath.manageProject)
                    }
                    break
                default:
                    reloadProcess()
                    break
            }
        } else {
            reloadProcess()
        }
    }
    const reloadProcess = () => {
        setConfigDialog({}) //clear function dialog
        let isSelectedCustomFolder = false
        if (appFolderAndTabStore?.folderSelected?.id) {
            isSelectedCustomFolder = tabAndFolderHook.isCustomFolder(appFolderAndTabStore.folderSelected.id)
        }

        let appFolderAndTabInfo = { ...appFolderAndTabStore }
        if (isSelectedCustomFolder) {
            appFolderAndTabInfo = {
                ...appFolderAndTabStore,
                folderSelected: {
                    id: null,
                    folderName_th: null,
                    folderName_en: null,
                    submittedID: null,
                    isLastProcess: false,
                },
            }
        }

        const isReloadProjectData = true
        const ignoreCustomFolder = true
        loadTabAndFolder(appFolderAndTabInfo, isReloadProjectData, ignoreCustomFolder)
        appFolderAndTabAction.resetTabDirty()
    }

    const handleClickBackToSearch = () => {
        history.goBack()
    }

    const handleClickCloseProject = () => {
        setConfigDialog({
            title: translate.get('LABEL.CLOSE_PROJECT'),
            content: React.createElement(CloseProjectOfficer, {
                onClickClose: () => {
                    dialogRef.current?.closeDialog()
                    setConfigDialog({})
                },
                onClickSave: handleSaveCloseProject,
            }),
            type: '',
        })
        dialogRef.current?.openDialog()
    }

    const handleSaveCloseProject = async (val: any) => {
        dialogRef.current?.closeDialog()
        const projectID = manageProjectStore?.projectData?.projectID ?? null
        const paramForSave: REQServiceType.CloseProjectByAdmin = {
            projectID: projectID,
            remark: val?.remark,
        }
        loadingHook.loadingOpen()
        const { status } = await callWebService.closeProjectByAdmin(paramForSave)
        loadingHook.loadingClose()

        if (status) {
            if (handleCallbackSaveComplete) handleCallbackSaveComplete(status, false)
        }
    }

    return {
        projectMode,
        isViewMode,
        configDialog,
        projectData: manageProjectStore.projectData,

        onRefDialog: onRefDialog,
        onCallbackSaveComplete: handleCallbackSaveComplete,
        onClickBackToSearch: handleClickBackToSearch,
        onClickCloseProject: handleClickCloseProject,
    }
}

export default useProjectProgressReportEditAfterReview
